import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  BookOpen, CheckCircle2, Circle, HelpCircle,
  ClipboardList, Wrench, Package, Calendar, FileText,
  Users, Building2, Layers, AlertTriangle, Shield, Mail, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

export default function HelpPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role || 'requester';
  const isAdmin = role === 'admin';

  // Go-Live checklist state (admin only)
  const [checklist, setChecklist] = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);

  const loadGoLiveChecklist = async () => {
    if (!isAdmin) return;
    setLoadingChecklist(true);
    try {
      const [branches, rooms, assets, users, wos, pmPlans, parts, slaPolicies, attachments] = await Promise.all([
        api.get('/branches'),
        api.get('/rooms'),
        api.get('/assets'),
        api.get('/users'),
        api.get('/work-orders'),
        api.get('/pm/plans'),
        api.get('/inventory/parts'),
        api.get('/sla-policies'),
        api.get('/attachments?entity_type=work_order&entity_id=any').catch(() => ({ data: [] }))
      ]);

      setChecklist({
        branches: branches.data?.length > 0,
        rooms: rooms.data?.length > 0,
        assets: assets.data?.length > 0,
        users: users.data?.length > 1,
        workOrders: wos.data?.length > 0,
        pmPlans: pmPlans.data?.length > 0,
        inventory: parts.data?.length > 0,
        slaPolicies: slaPolicies.data?.length > 0
      });
    } catch (err) {
      // Silently fail - checklist is optional
    } finally {
      setLoadingChecklist(false);
    }
  };

  useEffect(() => { 
    if (isAdmin) loadGoLiveChecklist(); 
  }, [isAdmin]);

  const CheckItem = ({ done, label }) => (
    <div className="flex items-center gap-3 py-2">
      {done ? (
        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
      ) : (
        <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
      )}
      <span className={done ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </div>
  );

  const WorkflowStep = ({ icon: Icon, title, description }) => (
    <div className="flex gap-4 py-3">
      <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-brand" />
      </div>
      <div>
        <h4 className="font-semibold text-foreground">{title}</h4>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
    </div>
  );

  return (
    <div data-testid="help-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight">
          {t('help.title')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{t('help.subtitle')}</p>
      </div>

      <Tabs defaultValue="workflows" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="workflows" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            {t('help.workflows')}
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            {t('help.commonTasks')}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="golive" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t('help.goLiveChecklist')}
            </TabsTrigger>
          )}
          <TabsTrigger value="install" className="flex items-center gap-2">
            <Smartphone className="w-4 h-4" />
            {t('pwa.installAppTitle')}
          </TabsTrigger>
        </TabsList>

        {/* Daily Workflows Tab */}
        <TabsContent value="workflows" className="space-y-6">
          {/* Role Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('help.yourRole')}:</span>
            <Badge variant="outline" className="capitalize">{role}</Badge>
          </div>

          {/* Requester Workflow */}
          {(role === 'requester' || role === 'admin' || role === 'supervisor') && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  {t('help.requesterWorkflow')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <WorkflowStep 
                  icon={FileText}
                  title={t('help.step1CreateRequest')}
                  description={t('help.step1Desc')}
                />
                <WorkflowStep 
                  icon={HelpCircle}
                  title={t('help.step2WaitReview')}
                  description={t('help.step2Desc')}
                />
                <WorkflowStep 
                  icon={CheckCircle2}
                  title={t('help.step3TrackProgress')}
                  description={t('help.step3Desc')}
                />
              </CardContent>
            </Card>
          )}

          {/* Technician Workflow */}
          {(role === 'technician' || role === 'admin' || role === 'supervisor') && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-green-500" />
                  {t('help.technicianWorkflow')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <WorkflowStep 
                  icon={ClipboardList}
                  title={t('help.techStep1')}
                  description={t('help.techStep1Desc')}
                />
                <WorkflowStep 
                  icon={Wrench}
                  title={t('help.techStep2')}
                  description={t('help.techStep2Desc')}
                />
                <WorkflowStep 
                  icon={Package}
                  title={t('help.techStep3')}
                  description={t('help.techStep3Desc')}
                />
                <WorkflowStep 
                  icon={CheckCircle2}
                  title={t('help.techStep4')}
                  description={t('help.techStep4Desc')}
                />
              </CardContent>
            </Card>
          )}

          {/* Supervisor Workflow */}
          {(role === 'supervisor' || role === 'admin') && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  {t('help.supervisorWorkflow')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <WorkflowStep 
                  icon={FileText}
                  title={t('help.supStep1')}
                  description={t('help.supStep1Desc')}
                />
                <WorkflowStep 
                  icon={Users}
                  title={t('help.supStep2')}
                  description={t('help.supStep2Desc')}
                />
                <WorkflowStep 
                  icon={AlertTriangle}
                  title={t('help.supStep3')}
                  description={t('help.supStep3Desc')}
                />
                <WorkflowStep 
                  icon={Layers}
                  title={t('help.supStep4')}
                  description={t('help.supStep4Desc')}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Common Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-barlow">{t('help.howTo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Import Codes */}
              {isAdmin && (
                <div className="border-b border-border pb-4">
                  <h4 className="font-semibold text-foreground mb-2">{t('help.importCodes')}</h4>
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>{t('help.importStep1')}</li>
                    <li>{t('help.importStep2')}</li>
                    <li>{t('help.importStep3')}</li>
                    <li>{t('help.importStep4')}</li>
                  </ol>
                </div>
              )}

              {/* Issue Parts */}
              {(role === 'admin' || role === 'supervisor' || role === 'technician') && (
                <div className="border-b border-border pb-4">
                  <h4 className="font-semibold text-foreground mb-2">{t('help.issueParts')}</h4>
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>{t('help.issueStep1')}</li>
                    <li>{t('help.issueStep2')}</li>
                    <li>{t('help.issueStep3')}</li>
                  </ol>
                </div>
              )}

              {/* Create PM Plan */}
              {(role === 'admin' || role === 'supervisor') && (
                <div className="border-b border-border pb-4">
                  <h4 className="font-semibold text-foreground mb-2">{t('help.createPMPlan')}</h4>
                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>{t('help.pmStep1')}</li>
                    <li>{t('help.pmStep2')}</li>
                    <li>{t('help.pmStep3')}</li>
                    <li>{t('help.pmStep4')}</li>
                  </ol>
                </div>
              )}

              {/* Interpret SLA */}
              {(role === 'admin' || role === 'supervisor') && (
                <div className="pb-2">
                  <h4 className="font-semibold text-foreground mb-2">{t('help.interpretSLA')}</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                    <li><span className="text-green-500 font-medium">{t('help.onTrack')}</span>: {t('help.onTrackDesc')}</li>
                    <li><span className="text-amber-500 font-medium">{t('help.responseBreached')}</span>: {t('help.responseBreachedDesc')}</li>
                    <li><span className="text-red-500 font-medium">{t('help.resolutionBreached')}</span>: {t('help.resolutionBreachedDesc')}</li>
                  </ul>
                </div>
              )}

              {/* Backup Info */}
              {isAdmin && (
                <div className="bg-muted/30 rounded-lg p-4 mt-4">
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    {t('help.backupPlan')}
                  </h4>
                  <p className="text-sm text-muted-foreground">{t('help.backupDesc')}</p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                    <li>{t('help.backupStep1')}</li>
                    <li>{t('help.backupStep2')}</li>
                    <li>{t('help.backupStep3')}</li>
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Go-Live Checklist Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="golive" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Shield className="w-5 h-5 text-brand" />
                  {t('help.goLiveTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingChecklist ? (
                  <p className="text-muted-foreground">{t('common.loading')}</p>
                ) : checklist ? (
                  <div className="space-y-1">
                    <CheckItem done={checklist.branches} label={t('help.checkBranches')} />
                    <CheckItem done={checklist.rooms} label={t('help.checkRooms')} />
                    <CheckItem done={checklist.assets} label={t('help.checkAssets')} />
                    <CheckItem done={checklist.users} label={t('help.checkUsers')} />
                    <Separator className="my-3" />
                    <CheckItem done={checklist.slaPolicies} label={t('help.checkSLA')} />
                    <CheckItem done={checklist.pmPlans} label={t('help.checkPM')} />
                    <CheckItem done={checklist.inventory} label={t('help.checkInventory')} />
                    <CheckItem done={checklist.workOrders} label={t('help.checkWO')} />
                    <Separator className="my-3" />
                    <div className="bg-muted/30 rounded-lg p-3 mt-4">
                      <p className="text-sm font-medium text-foreground">{t('help.manualChecks')}</p>
                      <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                        <li>{t('help.manualUpload')}</li>
                        <li>{t('help.manualPMGen')}</li>
                        <li>{t('help.manualIssueParts')}</li>
                        <li>{t('help.manualBackup')}</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">{t('help.checklistError')}</p>
                )}
              </CardContent>
            </Card>

            {/* Email Provider Setup */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  {t('help.emailSetupTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{t('help.emailSetupDesc')}</p>
                <div className="bg-muted/30 rounded-lg p-3 font-mono text-xs space-y-1">
                  <p>EMAIL_PROVIDER=resend</p>
                  <p>RESEND_API_KEY=re_xxxxxxxxx</p>
                  <p>RESEND_FROM_EMAIL=Unknown Escape Game &lt;maintenance@yourdomain.com&gt;</p>
                  <p>APP_BASE_URL=https://your-app-url.com</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">{t('help.emailResendNote')}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Install App Tab */}
        <TabsContent value="install" className="space-y-6">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-barlow flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-brand" />
                {t('pwa.installInstructions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{t('pwa.installDesc')}</p>
              
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">{t('pwa.iosTitle')}</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                  <li>{t('pwa.iosStep1')}</li>
                  <li>{t('pwa.iosStep2')}</li>
                  <li>{t('pwa.iosStep3')}</li>
                  <li>{t('pwa.iosStep4')}</li>
                </ol>
              </div>

              <Separator />

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">{t('pwa.androidTitle')}</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                  <li>{t('pwa.androidStep1')}</li>
                  <li>{t('pwa.androidStep2')}</li>
                  <li>{t('pwa.androidStep3')}</li>
                  <li>{t('pwa.androidStep4')}</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
